// src/components/sales/StaffAllowanceManager.js
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import SelectableCard from '../common/SelectableCard';
import { employeeService, productService, inventoryService, orderTrackingService, categoryService } from '../../services/api';
import { printStaffAllowanceReceipts } from '../../utils/PrintingUtils';
import { startTicketPrint } from '../../utils/ticketPrintDispatch';
import dedupeByName from '../../utils/dedupeByName';
import { translateDynamic, useLanguage, getTransliteration } from '../../context/LanguageContext';
import tenantStorage from '../../services/tenantStorage';
import ManagerGate from './ManagerGate';

const StaffAllowanceManager = ({
    cashierName,
    currency,
    tenantConfig,
    brandName,
    setError,
    setSuccess,
    printingEnabled,
    receiptTranslationsEnabled,
    receiptTranslationLanguage,
    hideButton = false,
    externalOpen = false,
}) => {
    const { t, language, dynamicTranslations, translationsLoaded, createReceiptTranslator } = useLanguage();

    const [isAllowanceModalOpen, setIsAllowanceModalOpen] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    // Manager approval for committing an allowance — see requestSaveAllowance.
    const [allowanceGateOpen, setAllowanceGateOpen] = useState(false);
    const [productCategories, setProductCategories] = useState({});
    const [activeCategory, setActiveCategory] = useState(null);
    const [loading, setLoading] = useState(false);

    // Load employees when component mounts
    useEffect(() => {
        const loadEmployees = async () => {
            try {
                const data = await employeeService.getAllEmployees();
                const activeEmployees = data.filter(emp => emp.status?.toLowerCase() === 'active');
                setEmployees(activeEmployees);
            } catch (err) {
                console.error('Failed to load employees:', err);
            }
        };
        loadEmployees();
    }, []);

    // Load products when modal opens
    useEffect(() => {
        if (isAllowanceModalOpen) {
            const loadProducts = async () => {
                try {
                    const data = await productService.getAllProducts();

                    // Group products by category
                    const categorized = data.reduce((acc, product) => {
                        if (!acc[product.category]) {
                            acc[product.category] = [];
                        }
                        acc[product.category].push(product);
                        return acc;
                    }, {});

                    setProducts(data);
                    setProductCategories(categorized);

                    // Set first category as active if there are categories
                    const categories = Object.keys(categorized);
                    if (categories.length > 0 && !activeCategory) {
                        setActiveCategory(categories[0]);
                    }
                } catch (err) {
                    setError(t('failed_to_load_products'));
                    console.error(err);
                }
            };

            loadProducts();
        }
    }, [isAllowanceModalOpen, setError, activeCategory, t]);

    // Helper function to calculate discounted price
    const getDiscountedPrice = (product) => {
        switch (product.discountType) {
            case 'fifty':
                return product.selling_price * 0.5;
            case 'hundred':
                return 0;
            default:
                return product.selling_price;
        }
    };

    const handleAddProduct = (product) => {
        const existingProduct = selectedProducts.find(p => p.id === product.product_code);

        if (existingProduct) {
            // Update quantity if product already exists
            setSelectedProducts(
                selectedProducts.map(p =>
                    p.id === product.product_code
                        ? {
                            ...p,
                            quantity: p.quantity + 1,
                            total: (p.quantity + 1) * getDiscountedPrice(p)
                        }
                        : p
                )
            );
        } else {
            // Add new product with discount tracking
            setSelectedProducts([
                ...selectedProducts,
                {
                    id: product.product_code,
                    name: product.name,
                    category: product.category,  // ADD THIS LINE
                    quantity: 1,
                    selling_price: product.selling_price,
                    discountType: 'none', // none, fifty, hundred
                    total: product.selling_price
                }
            ]);
        }
    };

    const handleDiscountChange = (productId, discountType) => {
        setSelectedProducts(
            selectedProducts.map(p => {
                if (p.id === productId) {
                    const unitPrice = getDiscountedPrice({ ...p, discountType });
                    return {
                        ...p,
                        discountType,
                        total: p.quantity * unitPrice
                    };
                }
                return p;
            })
        );
    };

    const handleQuantityChange = (productId, newQuantity) => {
        setSelectedProducts(
            selectedProducts.map(p => {
                if (p.id === productId) {
                    const qty = parseInt(newQuantity);
                    const unitPrice = getDiscountedPrice(p);
                    return {
                        ...p,
                        quantity: qty,
                        total: qty * unitPrice
                    };
                }
                return p;
            })
        );
    };

    // Remove product from allowance
    const handleRemoveProduct = (productId) => {
        setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    };

    // Calculate total for allowance
    const calculateTotal = () => {
        return selectedProducts.reduce((sum, product) => sum + product.total, 0);
    };

    // Deduct stock for staff allowance
    const deductStockForStaffAllowance = async (productCode, quantity) => {
        try {
            // First, get the product to check its stock calculation type
            const product = products.find(p => p.product_code === productCode);
            if (!product) {
                console.error(`Product ${productCode} not found`);
                return;
            }

            if (product.stock_calculation_type === 'auto') {
                // For auto-calculated products, deduct from raw ingredients
                console.log(`Deducting ingredients for auto-calculated product: ${productCode}`);

                // Call the API service to deduct ingredients
                await inventoryService.deductStockForStaffAllowance({
                    product_code: productCode,
                    quantity: quantity,
                    created_by: `Staff Allowance - ${selectedEmployee.name}`,
                    notes: `Staff allowance consumption for ${product.name}`
                });
            } else {
                // For manual stock products, deduct from product quantity
                console.log(`Deducting manual stock for product: ${productCode}`);

                if (product.quantity !== -9999) { // Don't deduct from infinite stock
                    const newQuantity = Math.max(0, product.quantity - quantity);
                    await productService.updateProduct(productCode, {
                        ...product,
                        quantity: newQuantity
                    });
                }
            }

            console.log(`Stock deducted successfully for ${productCode}`);

        } catch (error) {
            console.error(`Error deducting stock for product ${productCode}:`, error);
            // Don't fail the entire allowance process, just log the error
            setError(t('warning_stock_deduction_failed', { productCode }));
        }
    };

    // An allowance is written off against an employee's pay, so a manager has
    // to approve it before anything is committed (client request 2026-08-26).
    // The Save button only parks the request; handleSaveAllowance below is
    // unchanged and now runs solely from the gate's onApprove.
    const requestSaveAllowance = () => {
        if (!selectedEmployee || selectedProducts.length === 0) return;
        setAllowanceGateOpen(true);
    };

    const handleSaveAllowance = async () => {
        try {
            setLoading(true);

            const now = new Date();
            const today = now.toISOString().split('T')[0];
            const time = now.toTimeString().split(' ')[0];

            const salesDate = await tenantStorage.getState('salesDate');

            console.log("Selected products to save:", selectedProducts);

            const productsToPrint = selectedProducts.map(product => {
                return {
                    name: product.name,
                    quantity: product.quantity,
                    selling_price: product.discounted ? product.selling_price * 0.5 : product.selling_price,
                    category: product.category || "Other",  // SIMPLIFIED - use stored category
                    add_ons: [],
                    notes: `Staff Allowance for ${selectedEmployee.name}`
                };
            });

            // Print receipts BEFORE database operations
            const receiptData = {
                date: today,
                time: time,
                cashier: "Staff Allowance",
                paymentMethod: "Staff Allowance",
                diningOption: "Staff Allowance",
                tableNumber: selectedEmployee.name,
                products: productsToPrint,
                subtotal: calculateTotal(),
                discount: 0,
                serviceCharge: 0,
                total: calculateTotal(),
                notes: `Staff Allowance for ${selectedEmployee.name}`
            };

            try {
                console.log("Printing staff allowance receipts...");
                const tenantInfo = {
                    brandName,
                    currency,
                    printable_categories: tenantConfig?.printable_categories || [],
                    split_category_printing: tenantConfig?.split_category_printing || false,
                    printing: tenantConfig?.printing,
                    receipt_header: tenantConfig?.receipt_header,
                    receipt_footer: tenantConfig?.receipt_footer,
                    receipt_logo: tenantConfig?.receipt_logo
                };
                if (printingEnabled) {
                    const receiptT = createReceiptTranslator(receiptTranslationLanguage);
                    printStaffAllowanceReceipts(receiptData, productsToPrint, tenantInfo, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded);
                }
            } catch (printError) {
                console.error("Error printing staff allowance receipts:", printError);
            }
            
            // Save to database and deduct inventory
            for (const product of selectedProducts) {
                try {
                    const unitPrice = getDiscountedPrice(product);
                    const allowanceData = {
                        employee_id: selectedEmployee.employee_id,
                        date: today,
                        time: time,
                        product_code: product.id,
                        product_name: product.name,
                        quantity: product.quantity,
                        unit_price: unitPrice,
                        total_amount: product.total,
                        sales_date: salesDate || today
                    };

                    console.log("Saving allowance data:", allowanceData);
                    await employeeService.createEmployeeAllowance(allowanceData);

                    // Deduct stock for the consumed product
                    await deductStockForStaffAllowance(product.id, product.quantity);

                } catch (productError) {
                    console.error(`Error adding allowance for product ${product.name}:`, productError);
                    setError(t('failed_to_add_allowance_for_product', { product: product.name }));
                    setLoading(false);
                    return;
                }
            }

            // NEW: Create order tracking records for each printable category
            try {
                let printableCategories = [];
                try {
                    const cats = await categoryService.getAll();
                    printableCategories = dedupeByName(cats).filter(c => c.is_kitchen_display).map(c => c.name);
                } catch { /* fallback to empty */ }
                const trackingRecords = [];

                for (const category of printableCategories) {
                    const categoryItems = productsToPrint.filter(p => p.category === category);
                    if (categoryItems.length > 0) {
                        const trackingData = {
                            transaction_id: null, // Staff allowances don't have transaction IDs
                            order_id: null,       // Staff allowances don't have order IDs
                            category: category,
                            printed_categories: true,
                            items: categoryItems.map(item => ({
                                name: item.name,
                                quantity: item.quantity,
                                notes: item.notes || null,
                                add_ons: item.add_ons || []
                            })),
                            table_info: {
                                dining_option: "Staff Allowance",
                                table_number: selectedEmployee.name
                            },
                            total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                            notes: `Staff Allowance for ${selectedEmployee.name}`,
                            order_type: 'staff_allowance' // Special flag for staff allowances
                        };

                        try {
                            const trackingResult = await orderTrackingService.createOrderTracking(trackingData);
                            trackingRecords.push(trackingResult);
                            console.log(`Order tracking created for staff allowance ${category}:`, trackingResult.tracking_id);

                            // A staff meal's kitchen docket, dispatched exactly
                            // the way an ordinary sale's is (transactionFlow's
                            // dispatchKitchenTickets, drawerFlow's tracking
                            // build) — same helper, same arguments, same
                            // start-don't-await contract.
                            //
                            // Until now the staff-meal dockets went out through
                            // printStaffAllowanceReceipts above, under role
                            // 'receipt'. That role makes attemptDirectEpsonPrint
                            // look for a `transaction_id`, and a staff allowance
                            // creates NO transactions row — so on every network
                            // tenant the call found no identifier and returned,
                            // "falling back" to the Star CloudPRNT queue that no
                            // shop uses. Zero bytes ever reached the printer on
                            // iOS/Android, on either printing method: no shipping
                            // host has a silent-print bridge, so the usb branch's
                            // local print never fires on native either.
                            //
                            // These dockets were always TICKETS — per-category,
                            // titled "<CATEGORY> ORDER - STAFF ALLOWANCE",
                            // priceless, for the kitchen — so they belong on the
                            // ticket path, keyed off the OrderTracking record
                            // this function has already created and awaited. The
                            // tracking_id the ticket branch needs existed all
                            // along; it was simply never handed over.
                            //
                            // This is additive, not a replacement: printCategoryTicket
                            // returns immediately unless the tenant's method is
                            // 'network' AND the resolved printer speaks a
                            // direct protocol, which is precisely the case in
                            // which printStaffAllowanceReceipts prints nothing.
                            // A usb/Electron tenant keeps its existing local
                            // print and gains no second docket.
                            if (trackingResult?.tracking_id) {
                                startTicketPrint(category, trackingResult.tracking_id, tenantConfig);
                            }

                            // Dispatch cross-tab event to notify OrderViewPage about new staff allowance
                            const eventData = {
                                type: 'orderSubmitted',
                                category: category,
                                orderType: 'staff_allowance',
                                orderId: null,
                                trackingId: trackingResult.tracking_id,
                                timestamp: Date.now()
                            };

                            console.log(` DISPATCHING cross-tab orderSubmitted event for staff allowance category: ${category}`);
                            console.log(' Event data:', eventData);

                            // Use localStorage to trigger cross-tab events
                            localStorage.setItem('orderEvent', JSON.stringify(eventData));
                            localStorage.removeItem('orderEvent'); // Trigger the storage event

                            console.log(' Cross-tab event dispatched successfully');
                        } catch (trackingError) {
                            console.error(`Failed to create tracking for staff allowance ${category}:`, trackingError);
                            // Don't fail the entire process if tracking fails
                        }
                    }
                }

                // Handle items not in printable categories
                const otherItems = productsToPrint.filter(p =>
                    !printableCategories.includes(p.category)
                );
                if (otherItems.length > 0) {
                    const trackingData = {
                        transaction_id: null,
                        order_id: null,
                        category: "Other",
                        printed_categories: true,
                        items: otherItems.map(item => ({
                            name: item.name,
                            quantity: item.quantity,
                            notes: item.notes || null,
                            add_ons: item.add_ons || []
                        })),
                        table_info: {
                            dining_option: "Staff Allowance",
                            table_number: selectedEmployee.name
                        },
                        total_items_count: otherItems.reduce((sum, item) => sum + item.quantity, 0),
                        notes: `Staff Allowance for ${selectedEmployee.name}`,
                        order_type: 'staff_allowance'
                    };

                    try {
                        const trackingResult = await orderTrackingService.createOrderTracking(trackingData);
                        trackingRecords.push(trackingResult);
                        console.log(`Order tracking created for staff allowance Other items:`, trackingResult.tracking_id);

                        // Deliberately NO startTicketPrint here. "Other" is the
                        // bucket for items whose category is NOT kitchen-display
                        // — the shop has said these do not go to the kitchen.
                        // An ordinary sale of the very same product creates no
                        // tracking record and prints no docket at all
                        // (dispatchKitchenTickets loops printableCategories and
                        // has no catch-all), so dispatching one here would make
                        // a staff meal produce MORE paper than a paying
                        // customer's order for the identical item. It would
                        // also land on the shared kitchen printer, since
                        // ticketRoleCandidates falls "Other" back to 'kitchen'
                        // after the backend rejects it as a role.
                        //
                        // The record itself is still created, unchanged: it is
                        // what puts the allowance on the KDS and in the
                        // tenant's history.

                        // Dispatch event for other items
                        const eventData = {
                            type: 'orderSubmitted',
                            category: "Other",
                            orderType: 'staff_allowance',
                            orderId: null,
                            trackingId: trackingResult.tracking_id,
                            timestamp: Date.now()
                        };

                        localStorage.setItem('orderEvent', JSON.stringify(eventData));
                        localStorage.removeItem('orderEvent');
                    } catch (trackingError) {
                        console.error(`Failed to create tracking for staff allowance Other items:`, trackingError);
                    }
                }

                if (trackingRecords.length > 0) {
                    console.log(`Created ${trackingRecords.length} order tracking records for staff allowance:`,
                        trackingRecords.map(r => `${r.category}: ${r.tracking_id}`));
                }

            } catch (trackingError) {
                console.error('Error creating order tracking for staff allowance:', trackingError);
                // Don't fail the entire process if tracking fails
            }

            setSuccess(t('allowance_added_successfully_stock_updated_tracking_created'));

            // Reset modal state
            setIsAllowanceModalOpen(false);
            setSelectedEmployee(null);
            setSelectedProducts([]);
            setActiveCategory(null);
            setLoading(false);

        } catch (err) {
            setError(t('failed_to_add_allowance'));
            console.error("Error in handleSaveAllowance:", err);
            setLoading(false);
        }
    };

    // Allow parent to open the modal externally
    useEffect(() => {
        if (externalOpen) {
            handleOpenAllowance();
        }
    }, [externalOpen]);

    const handleOpenAllowance = () => {
        setSelectedEmployee(null);
        setSelectedProducts([]);
        setActiveCategory(null);
        setIsAllowanceModalOpen(true);
    };

    return (
        <>
            {/* Staff Allowance Button */}
            {!hideButton && (
                <button
                    onClick={handleOpenAllowance}
                    style={{
                        padding: '4px 12px', borderRadius: 6,
                        fontSize: 11, fontWeight: 700,
                        background: '#2563eb', color: '#fff',
                        border: 'none', cursor: 'pointer',
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        letterSpacing: 0.2, lineHeight: 1,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb'; }}
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 11h18" />
                        <path d="M12 19H5a2 2 0 0 1-2-2v-2h18v2a2 2 0 0 1-2 2h-3" />
                        <path d="M4 11V9a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
                        <line x1="12" y1="19" x2="12" y2="15" />
                        <line x1="8" y1="15" x2="8" y2="11" />
                        <line x1="16" y1="15" x2="16" y2="11" />
                    </svg>
                    {t('staff_allowance')}
                </button>
            )}

            {/* Staff Allowance Modal */}
            <Modal
                isOpen={isAllowanceModalOpen}
                onClose={() => {
                    setIsAllowanceModalOpen(false);
                    setSelectedEmployee(null);
                    setSelectedProducts([]);
                    setActiveCategory(null);
                }}
                title={t('staff_allowance')}
                maxWidth="max-w-4xl"
            >
                <div className="space-y-6">
                    {/* Employee Selection */}
                    {!selectedEmployee && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-3">{t('select_employee')}</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                                {employees.map(employee => (
                                    <SelectableCard
                                        key={employee.employee_id}
                                        label={`${employee.name} - ${translateDynamic(employee.role, t)}`}
                                        selected={false}
                                        onClick={() => setSelectedEmployee(employee)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Product Selection */}
                    {selectedEmployee && (
                        <>
                            <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-sm font-medium text-blue-800">
                                    {t('staff_allowance_for')}: {selectedEmployee.name} ({translateDynamic(selectedEmployee.role, t)})
                                </p>
                                <button
                                    onClick={() => {
                                        setSelectedEmployee(null);
                                        setSelectedProducts([]);
                                        setActiveCategory(null);
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 mt-1"
                                >
                                    {t('change_employee')}
                                </button>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">{t('available_products')}</h3>

                                {/* Category tabs */}
                                <div className="mb-4 border-b border-gray-200">
                                    <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Product Categories">
                                        {Object.keys(productCategories).map(category => (
                                            <button
                                                key={category}
                                                onClick={() => setActiveCategory(category)}
                                                className={`whitespace-nowrap pb-2 px-1 border-b-2 font-medium text-sm ${activeCategory === category
                                                    ? 'border-primary-500 text-primary-600'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                    }`}
                                            >
                                                {translateDynamic(category, t)}
                                            </button>
                                        ))}
                                    </nav>
                                </div>

                                {/* Products grid */}
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {activeCategory && productCategories[activeCategory]?.map(product => (
                                        <button
                                            key={product.product_code}
                                            onClick={() => handleAddProduct(product)}
                                            className="p-2 border border-gray-200 rounded-md text-sm text-left hover:bg-gray-100"
                                        >
                                            <p className="font-medium">{getTransliteration(product.name, language, t)}</p>
                                            <p className="text-gray-600">{currency}{product.selling_price.toFixed(2)}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {selectedProducts.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">{t('selected_products')}</h3>
                                    <div className="border rounded-md max-h-60 overflow-y-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t('product')}
                                                    </th>
                                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t('price')}
                                                    </th>
                                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t('quantity')}
                                                    </th>
                                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t('total')}
                                                    </th>
                                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t('actions')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {selectedProducts.map(product => (
                                                    <tr key={product.id}>
                                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                                            {product.name}
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                                            <div className="flex flex-col">
                                                                <span className={product.discountType !== 'none' ? 'line-through text-gray-400 text-xs' : ''}>
                                                                    {currency}{product.selling_price.toFixed(2)}
                                                                </span>
                                                                {product.discountType === 'fifty' && (
                                                                    <span className="text-green-600 text-xs">
                                                                        {currency}{(product.selling_price * 0.5).toFixed(2)} ({t('fifty_percent_off')})
                                                                    </span>
                                                                )}
                                                                {product.discountType === 'hundred' && (
                                                                    <span className="text-green-600 text-xs">
                                                                        {currency}0.00 ({t('hundred_percent_off')})
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={product.quantity}
                                                                onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                                                                className="w-16 border border-gray-300 rounded px-2 py-1"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                                            {currency}{product.total.toFixed(2)}
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                                            <div className="flex flex-col space-y-1">
                                                                <button
                                                                    onClick={() => handleRemoveProduct(product.id)}
                                                                    className="text-red-600 hover:text-red-900 text-xs"
                                                                >
                                                                    {t('remove')}
                                                                </button>
                                                                <div className="flex items-center space-x-1">
                                                                    <select
                                                                        value={product.discountType}
                                                                        onChange={(e) => handleDiscountChange(product.id, e.target.value)}
                                                                        className="text-xs border border-gray-300 rounded px-1 py-0.5"
                                                                    >
                                                                        <option value="none">{t('no_discount')}</option>
                                                                        <option value="fifty">{t('fifty_percent_off')}</option>
                                                                        <option value="hundred">{t('hundred_percent_off')}</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr className="bg-gray-50">
                                                    <td colSpan="3" className="px-3 py-2 text-sm font-medium text-gray-700 text-right">
                                                        {t('total')}:
                                                    </td>
                                                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {currency}{calculateTotal().toFixed(2)}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={() => {
                                setIsAllowanceModalOpen(false);
                                setSelectedEmployee(null);
                                setSelectedProducts([]);
                                setActiveCategory(null);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={requestSaveAllowance}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                            disabled={!selectedEmployee || selectedProducts.length === 0 || loading}
                        >
                            {loading ? t('saving') : t('save_allowance')}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Manager approval before the allowance is committed. */}
            <ManagerGate
                open={allowanceGateOpen}
                action="allowance"
                context={{
                    employeeId: selectedEmployee?.employee_id || null,
                    employeeName: selectedEmployee?.name || null,
                    total: calculateTotal(),
                }}
                onApprove={async () => {
                    setAllowanceGateOpen(false);
                    await handleSaveAllowance();
                }}
                onCancel={() => setAllowanceGateOpen(false)}
            />
        </>
    );
};

export default StaffAllowanceManager;